import { useState, useEffect } from 'react';
import { X, Eye, CheckCircle, XCircle, AlertCircle, Mail } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { sanitizeHtml } from '../../lib/sanitize';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';

interface EmailHistoryProps {
  onClose: () => void;
}

interface EmailRecord {
  id: string;
  template_nombre: string | null;
  destinatarios_count: number;
  lista_destinatarios_ids: string[];
  asunto: string;
  contenido_html: string;
  enviado_en: string;
  estado: 'exitoso' | 'fallido' | 'parcial';
  detalles: {
    successCount: number;
    failureCount: number;
    failedRecipients?: string[];
  };
}

interface Profile {
  id: string;
  email: string;
  full_name: string;
}

export default function EmailHistory({ onClose }: EmailHistoryProps) {
  const [records, setRecords] = useState<EmailRecord[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<EmailRecord | null>(null);
  const [profiles, setProfiles] = useState<{ [key: string]: Profile }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  useEffect(() => {
    loadHistory();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const loadHistory = async () => {
    const { data, error } = await supabase
      .from('email_history')
      .select('*')
      .order('enviado_en', { ascending: false })
      .limit(100);

    if (!error && data) {
      setRecords(data);

      const allUserIds = new Set<string>();
      data.forEach((record) => {
        record.lista_destinatarios_ids.forEach((id: string) => allUserIds.add(id));
      });

      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .in('id', Array.from(allUserIds));

      if (profilesData) {
        const profilesMap: { [key: string]: Profile } = {};
        profilesData.forEach((profile) => {
          profilesMap[profile.id] = profile;
        });
        setProfiles(profilesMap);
      }
    }
  };

  const totalPages = Math.ceil(records.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedRecords = records.slice(startIndex, startIndex + recordsPerPage);

  const getStatusIcon = (estado: string) => {
    switch (estado) {
      case 'exitoso':
        return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'fallido':
        return <XCircle className="w-4 h-4 text-rose-400" />;
      case 'parcial':
        return <AlertCircle className="w-4 h-4 text-amber-400" />;
      default:
        return null;
    }
  };

  const getStatusText = (estado: string) => {
    switch (estado) {
      case 'exitoso': return 'Exitoso';
      case 'fallido': return 'Fallido';
      case 'parcial': return 'Parcial';
      default: return estado;
    }
  };

  const getStatusChip = (estado: string) => {
    switch (estado) {
      case 'exitoso':
        return 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-400/10 text-emerald-300 border border-emerald-400/20';
      case 'fallido':
        return 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-rose-400/10 text-rose-300 border border-rose-400/20';
      case 'parcial':
        return 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-400/10 text-amber-300 border border-amber-400/20';
      default:
        return 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-white/60 border border-white/10';
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-history-title"
    >
      <div className="glass-panel rounded-2xl border border-white/[0.06] w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
          <h2 id="email-history-title" className="text-xl font-semibold text-white">
            Historial de Correos
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/[0.06] text-white/50 hover:text-white transition-colors"
            aria-label="Cerrar historial"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {records.length === 0 ? (
            <EmptyState
              icon={Mail}
              title="Sin correos enviados"
              description="El historial de correos enviados aparecerá aquí."
            />
          ) : selectedRecord ? (
            /* ── Detail View ── */
            <div className="space-y-5">
              <button
                onClick={() => setSelectedRecord(null)}
                className="text-cubus-lime hover:text-cubus-lime/80 text-sm font-medium transition-colors"
              >
                ← Volver al listado
              </button>

              <div className="glass-surface rounded-2xl border border-white/[0.06] p-6 space-y-5">
                {/* Subject */}
                <div>
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-1">Asunto</p>
                  <p className="text-white text-lg font-semibold">{selectedRecord.asunto}</p>
                </div>

                {/* Meta grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-1">Fecha de Envío</p>
                    <p className="text-white text-sm">
                      {new Date(selectedRecord.enviado_en).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-1">Estado</p>
                    <span className={getStatusChip(selectedRecord.estado)}>
                      {getStatusIcon(selectedRecord.estado)}
                      {getStatusText(selectedRecord.estado)}
                    </span>
                  </div>
                </div>

                {/* Stats */}
                <div>
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">Estadísticas</p>
                  <div className="flex gap-3">
                    <div className="flex-1 glass-deep rounded-xl border border-emerald-400/20 px-4 py-3">
                      <p className="text-emerald-300 text-xs mb-1">Exitosos</p>
                      <p className="text-white text-2xl font-bold">{selectedRecord.detalles.successCount}</p>
                    </div>
                    <div className="flex-1 glass-deep rounded-xl border border-rose-400/20 px-4 py-3">
                      <p className="text-rose-300 text-xs mb-1">Fallidos</p>
                      <p className="text-white text-2xl font-bold">{selectedRecord.detalles.failureCount}</p>
                    </div>
                    <div className="flex-1 glass-deep rounded-xl border border-blue-400/20 px-4 py-3">
                      <p className="text-blue-300 text-xs mb-1">Total</p>
                      <p className="text-white text-2xl font-bold">{selectedRecord.destinatarios_count}</p>
                    </div>
                  </div>
                </div>

                {/* Recipients */}
                <div>
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">Destinatarios</p>
                  <div className="glass-deep rounded-xl border border-white/[0.06] p-3 max-h-48 overflow-y-auto space-y-2">
                    {selectedRecord.lista_destinatarios_ids.map((userId) => {
                      const profile = profiles[userId];
                      const isFailed = selectedRecord.detalles.failedRecipients?.includes(profile?.email);
                      return (
                        <div
                          key={userId}
                          className={`flex items-center justify-between p-2.5 rounded-lg border ${
                            isFailed
                              ? 'bg-rose-500/10 border-rose-500/20'
                              : 'bg-emerald-500/10 border-emerald-500/20'
                          }`}
                        >
                          <div>
                            <p className="text-white text-sm font-medium">
                              {profile?.full_name || 'Usuario desconocido'}
                            </p>
                            <p className="text-white/50 text-xs">{profile?.email}</p>
                          </div>
                          {isFailed ? (
                            <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Email preview — white bg intentional for email readability */}
                <div>
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-3">Vista Previa del Correo</p>
                  <div className="bg-white rounded-xl p-6 max-h-96 overflow-y-auto">
                    <div
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedRecord.contenido_html) }}
                      className="prose max-w-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* ── List View ── */
            <div>
              <div className="glass-surface rounded-2xl border border-white/[0.06] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left py-3 px-4 text-white/50 text-xs font-semibold uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="text-left py-3 px-4 text-white/50 text-xs font-semibold uppercase tracking-wider">
                        Asunto
                      </th>
                      <th className="text-center py-3 px-4 text-white/50 text-xs font-semibold uppercase tracking-wider">
                        Destinatarios
                      </th>
                      <th className="text-center py-3 px-4 text-white/50 text-xs font-semibold uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="text-center py-3 px-4 text-white/50 text-xs font-semibold uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRecords.map((record, i) => (
                      <tr
                        key={record.id}
                        className={`border-b border-white/[0.04] hover:bg-white/[0.04] transition-colors ${
                          i === paginatedRecords.length - 1 ? 'border-b-0' : ''
                        }`}
                      >
                        <td className="py-3 px-4">
                          <p className="text-white text-sm">
                            {new Date(record.enviado_en).toLocaleDateString()}
                          </p>
                          <p className="text-white/50 text-xs">
                            {new Date(record.enviado_en).toLocaleTimeString()}
                          </p>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-white font-medium truncate max-w-xs">{record.asunto}</p>
                          {record.template_nombre && (
                            <p className="text-white/50 text-xs mt-0.5">
                              Plantilla: {record.template_nombre}
                            </p>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="text-white font-semibold">{record.destinatarios_count}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={getStatusChip(record.estado)}>
                            {getStatusIcon(record.estado)}
                            {getStatusText(record.estado)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            variant="glass"
                            size="sm"
                            leftIcon={<Eye className="w-3.5 h-3.5" />}
                            onClick={() => setSelectedRecord(record)}
                          >
                            Ver Detalles
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-6">
                  <Button
                    variant="glass"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>
                  <span className="text-white/60 text-sm">
                    Página {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="glass"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
